import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import CreatableSelect from 'react-select/creatable';
import Tippy from '@tippyjs/react';
import FormSelect from '../form-select';
import SelectTable from '../select-table';
import { useSaleFormContext } from './SaleFormContext';
registerLocale('el', el);

const CREATABLE_STYLES = {
    indicatorsContainer: (provided) => ({ ...provided, padding: '0px' }),
    placeholder: (provided) => ({ ...provided, fontSize: '12px' }),
    control: (provided) => ({ ...provided, fontSize: '14px' }),
};

function InvoiceHeaderCard({
    onChangeBranch,
    branches,
    onChangeInvoiceType,
    invoiceTypes,
    onChangeCorrelativeInvoice,
    onChangeConnectedInvoice,
    onInputChangeConnectedInvoice,
    loadPageOptions,
    selectedInvoiceDate,
    maxInvoiceDate,
    onChangeDate,
    onChangeSeries,
    seriesOptions,
    isCredit,
    isDeliveryNoteChange,
}) {
    const { t, i18n } = useTranslation();
    const { isFieldDisabled, isFieldRequired, isVisible } = useSaleFormContext();
    const { register, control, getValues, formState: { errors } } = useFormContext();

    return (
        <div className="card mainInvoiceInfoCard">
            {/* <div className="card-header">
                <i className="fa fa-file-text-o" style={{ marginRight: 6 }}></i>
                <span style={{ fontWeight: 600 }}>{t("invoicePublice")}</span>
            </div> */}
            <div className="card-body">
                <div className="row g-2 align-items-end">

                    <div className="col-md-2 selectInputContainer form-group required">
                        <label className="control-label inputlabel">
                            {t('Branch')}
                            <Tippy trigger="click" content={<span className="tooltip-skin">{t("saleBranchInfo")}</span>}>
                                <i className="fa fa-x2 fa-info-circle infoicon"></i>
                            </Tippy>
                        </label>
                        <Controller
                            render={({ field }) => (
                                <FormSelect
                                    field={field}
                                    onChange={onChangeBranch}
                                    isDisabled={isFieldDisabled("branch")}
                                    inputId="branch-react-select"
                                    options={branches}
                                    error={errors.branch}
                                    withMenu
                                />
                            )}
                            control={control}
                            rules={{ required: true }}
                            name="branch"
                            defaultValue=""
                        />
                        <div className="invalid-feedback">{errors.branch?.message}</div>
                    </div>

                    <div className="col-md-2 selectInputContainer form-group required">
                        <label className="control-label inputlabel">
                            {t('InvoiceType')}
                            <Tippy trigger="click" content={<span className="tooltip-skin">{t("invoiceTypeInfo")}</span>}>
                                <i className="fa fa-x2 fa-info-circle infoicon"></i>
                            </Tippy>
                        </label>
                        <Controller
                            render={({ field }) => (
                                <FormSelect
                                    field={field}
                                    onChange={onChangeInvoiceType}
                                    inputId="invoiceType-react-select"
                                    id="invoiceType"
                                    options={invoiceTypes}
                                    isDisabled={isFieldDisabled("invoiceType")}
                                    error={errors.invoiceType}
                                    withMenu
                                />
                            )}
                            control={control}
                            rules={{ required: true }}
                            name="invoiceType"
                            defaultValue=""
                        />
                        <div className="invalid-feedback">{errors.invoiceType?.message}</div>
                    </div>

                    <div
                        className={`col-md-2 selectInputContainer form-group correlativeSales ${isFieldRequired("correlativeSales") ? "required" : ""}`}
                        style={{ display: isVisible('correlativeSales') ? 'unset' : 'none' }}
                    >
                        <label className="control-label inputlabel">{t('correlativeSales')}</label>
                        <Controller
                            render={({ field, ref }) => (
                                <SelectTable {...field}
                                    menuWidth={500}
                                    ref={ref}
                                    onChangeValue={onChangeCorrelativeInvoice}
                                    onChange={onChangeCorrelativeInvoice}
                                    loadOptions={loadPageOptions}
                                    columns={[
                                        { header: t("invoiceNumber"), field: "code", flex: 2 },
                                        { header: t("InvoiceType"), field: "invoiceType", flex: 6 },
                                        { header: t("mark"), field: "mark", asLabel: true, flex: 4 },
                                    ]}
                                    selectedInvoiceType={getValues().invoiceType}
                                    asyncPaginate={true}
                                    isClearable={true}
                                    key={new Date().getTime()}
                                    className={`form-control ${errors.correlativeSales ? 'is-invalid' : ''}`}
                                    placeholder={t('Select')}
                                />
                            )}
                            control={control}
                            rules={{ required: isFieldRequired("correlativeSales") }}
                            name="correlativeSales"
                            defaultValue=""
                        />
                        <div className="invalid-feedback">{errors.correlativeSales?.message}</div>
                    </div>

                    <div
                        className={`col-md-2 selectInputContainer form-group connectedSales ${isFieldRequired("connectedSales") ? "required" : ""}`}
                        style={{ display: isVisible('connectedSales') ? 'unset' : 'none' }}
                    >
                        <label className="control-label inputlabel">{t('connectedSales')}</label>
                        <Controller
                            render={({ field, ref }) => (
                                <SelectTable {...field}
                                    menuWidth={500}
                                    ref={ref}
                                    onChangeValue={onChangeConnectedInvoice}
                                    onChange={onChangeConnectedInvoice}
                                    onInputChange={onInputChangeConnectedInvoice}
                                    loadOptions={loadPageOptions}
                                    columns={[
                                        { header: t("invoiceNumber"), field: "code", flex: 2 },
                                        { header: t("InvoiceType"), field: "invoiceType", flex: 6 },
                                        { header: t("mark"), field: "mark", asLabel: true, flex: 4 },
                                    ]}
                                    selectedInvoiceType={getValues().invoiceType}
                                    asyncPaginate={true}
                                    isMulti={true}
                                    isClearable={true}
                                    key={new Date().getTime()}
                                    className={`form-control ${errors.connectedSales ? 'is-invalid' : ''}`}
                                    placeholder={t('Select')}
                                />
                            )}
                            control={control}
                            rules={{ required: isFieldRequired("connectedSales") }}
                            name="connectedSales"
                            defaultValue=""
                        />
                        <div className="invalid-feedback">{errors.connectedSales?.message}</div>
                    </div>

                    <div className="col-md-2 form-group required">
                        <label className="control-label inputlabel">
                            {t('issuerDate')}
                            <Tippy trigger="click" content={<span className="tooltip-skin">{t("invoiceDateInfo")}</span>}>
                                <i className="fa fa-x2 fa-info-circle infoicon"></i>
                            </Tippy>
                        </label>
                        <Controller
                            render={({ field }) => (
                                <DatePicker {...field}
                                    selected={selectedInvoiceDate}
                                    minDate={new Date("01/01/2020")}
                                    maxDate={maxInvoiceDate}
                                    onChange={onChangeDate}
                                    disabled={isFieldDisabled("invoiceDate")}
                                    dateFormat="dd/MM/yyyy HH:mm"
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals="1"
                                    autoComplete="off"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    locale={i18n.language === 'el-GR' || i18n.language === 'gr' ? 'el' : undefined}
                                    className={`form-control ${errors.invoiceDate ? 'is-invalid' : ''}`}
                                />
                            )}
                            control={control}
                            rules={{ required: true }}
                            name="invoiceDate"
                            defaultValue=""
                        />
                        <div className="invalid-feedback">{errors.invoiceDate?.message}</div>
                    </div>

                    <div
                        className="col-md-4 afterinvoice"
                        style={{ display: isVisible('afterinvoice') ? 'block' : 'none' }}
                    >
                        <div className="d-flex gap-2 align-items-end">
                            <div className="flex-grow-1 selectInputContainer form-group required">
                                <label className="control-label inputlabel">
                                    {t('Series')}
                                    <Tippy trigger="click" content={<span className="tooltip-skin">{t("seriesInfo")}</span>}>
                                        <i className="fa fa-x2 fa-info-circle infoicon"></i>
                                    </Tippy>
                                </label>
                                <Controller
                                    render={({ field }) => (
                                        <CreatableSelect {...field}
                                            styles={CREATABLE_STYLES}
                                            onChange={onChangeSeries}
                                            inputId="series-react-select"
                                            options={seriesOptions}
                                            isDisabled={isFieldDisabled("series") && !isCredit}
                                            className={`form-control ${errors.series ? 'is-invalid' : ''}`}
                                            placeholder={t('Select')}
                                        />
                                    )}
                                    disabled={isFieldDisabled("series")}
                                    control={control}
                                    rules={{ required: true }}
                                    name="series"
                                    defaultValue=""
                                />
                                <div className="invalid-feedback">{errors.series?.message}</div>
                            </div>

                            <div className="invoice-number-field form-group required">
                                <label className="control-label inputlabel">
                                    {t("invoiceNumber")}
                                    <Tippy trigger="click" content={<span className="tooltip-skin">{t("saleSeriesInfo")}</span>}>
                                        <i className="fa fa-x2 fa-info-circle infoicon"></i>
                                    </Tippy>
                                </label>
                                <div className="input-group">
                                    <div className="input-group-prepend">
                                        <span className="input-group-text">#</span>
                                    </div>
                                    <input
                                        name="invoiceNumber"
                                        type="text"
                                        maxLength={11}
                                        readOnly={true}
                                        disabled
                                        {...register('invoiceNumber', { required: true, maxLength: 11 })}
                                        className={`form-control ${errors.invoiceNumber ? 'is-invalid' : ''}`}
                                    />
                                    <div className="invalid-feedback">{errors.invoiceNumber?.message}</div>
                                </div>
                            </div>

                            <div className="delivery-note-toggle form-group" id="isDeliveryNote">
                                <label className="control-label inputlabel d-block">{t("isDeliveryNote")}</label>
                                <div className="form-switch">
                                    <input
                                        className="form-check-input"
                                        {...register('isDeliveryNote')}
                                        disabled={isFieldDisabled("isDeliveryNote")}
                                        onChange={isDeliveryNoteChange}
                                        name="isDeliveryNote"
                                        type="checkbox"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default InvoiceHeaderCard;
